import { FC } from 'react';
import type { AssessmentRunProgressSummary } from '@libs/types';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { AssessmentStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import { isWithinStartupGrace } from '../../utils/run-progress';

import { inFlightFill, noDataFill, pendingFill } from './RunProgressParts';

/**
 * The list row's version of the run bar.
 *
 * Same three rules as the full screen, at a tenth the size: solid green for
 * documents assessed, hatched for documents in flight, and a DASHED TRACK when
 * the run reports nothing at all.
 *
 * That third variant is the reason this component exists rather than a
 * `<LinearProgress value={n} />`. The row used to render a number interpolated
 * from elapsed time, which advanced identically whether the run was working or
 * had died in its first minute — so the one state a watching user most needs to
 * recognise was the one the bar was incapable of showing.
 */
export const CompactRunBar: FC<{
  progress: AssessmentRunProgressSummary;
  /**
   * Distinguishes "queued, nothing has started" from "running, and we cannot
   * see it". Both draw the same dashed track — there is no progress to fill it
   * with either way — but only one of them is a reason to worry, so they must
   * not share a caption.
   */
  status: AssessmentStatus;
}> = observer(({ progress, status }) => {
  const theme = useTheme();
  const { t } = useTrans();

  if (!progress.reporting) {
    // Waiting rather than missing: not picked up yet, or dispatched recently
    // enough that the Job is still being scheduled and cold-started. Only
    // silence past that window is worth captioning as a blind spot.
    //
    // The browser's clock, not the server's — unlike the run screen, which
    // ages `lastEventAt` against `serverTime` because it draws a stall warning
    // and offers a restart. This is a caption on a list row, and a clock
    // skewed by ten minutes changes which of two neutral phrases it shows.
    const pending =
      status === AssessmentStatus.SUBMITTED || isWithinStartupGrace(progress.startedAt, Date.now());

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625, minWidth: 0 }}>
        <Box
          sx={{
            height: 8,
            borderRadius: 9999,
            border: `1px dashed ${pending ? theme.palette.ocean[300] : theme.palette.grey[600]}`,
            background: pending ? pendingFill(theme) : noDataFill(theme),
          }}
        />
        <Typography sx={{ fontSize: 11, color: 'text.tertiary' }}>
          {pending
            ? t(
                status === AssessmentStatus.SUBMITTED
                  ? 'app.assessment.progress.counter.queued'
                  : 'app.assessment.progress.startingUp',
              )
            : t('app.assessment.progress.noProgressData')}
        </Typography>
      </Box>
    );
  }

  const total = Math.max(1, progress.total);
  const done = Math.min(100, (progress.assessed / total) * 100);
  // Clamped against `done` so the two fills can never sum past the track: the
  // counters are written by separate events and a late arrival can briefly
  // leave them inconsistent.
  const flight = Math.max(0, Math.min(100 - done, (progress.inFlight / total) * 100));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625, minWidth: 0 }}>
      <Box
        sx={{
          height: 8,
          borderRadius: 9999,
          backgroundColor: theme.palette.grey[300],
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <Box
          sx={{
            width: `${done}%`,
            backgroundColor: theme.palette.accent.green,
            transition: 'width 400ms ease',
          }}
        />
        <Box
          sx={{
            width: `${flight}%`,
            background: inFlightFill(theme),
            transition: 'width 400ms ease',
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 11, color: 'text.tertiary', fontVariantNumeric: 'tabular-nums' }}>
        {progress.inFlight > 0
          ? t('app.assessment.progress.countedInFlight', {
              done: progress.assessed,
              total: progress.total,
              flight: progress.inFlight,
            })
          : t('app.assessment.progress.countedOf', {
              done: progress.assessed,
              total: progress.total,
            })}
      </Typography>
    </Box>
  );
});
